/** Generated from Kysely types - DO NOT EDIT MANUALLY */
/** Run: bun run generate:zero to regenerate this file */

import {
	boolean,
	createSchema,
	json,
	number,
	relationships,
	string,
	table,
	type Row,
} from "@rocicorp/zero"
import type { MeshData } from "@oblique/shared/proto"
import type { ReleasePlatforms } from "@oblique/shared/types"

// ============================================
// Table Definitions
// ============================================

type ReadonlyJson<T> = T extends undefined
	? undefined
	: T extends null | string | number | boolean
		? T
		: T extends (infer U)[]
			? readonly ReadonlyJson<U>[]
			: T extends object
				? { readonly [K in keyof T]: ReadonlyJson<T[K]> }
				: never

// Original Kysely type: Geometries
const geometries = table("geometries")
	.columns({
		completed_at: number().optional(),
		created_at: number().optional(),
		error: string().optional(),
		execution_time_ms: number().optional(),
		id: string(),
		mesh_data: json<ReadonlyJson<MeshData>>().optional(),
		metadata: json().optional(),
		parameters: json(),
		project_id: string().optional(),
		project_node_id: string().optional(),
		serialized_data: string().optional(),
		status: string(),
		type: string().optional(),
		user_id: string(),
	})
	.primaryKey("id")

// Original Kysely type: ProjectEdges
const project_edges = table("project_edges")
	.columns({
		created_at: number().optional(),
		id: string(),
		metadata: json(),
		project_id: string(),
		source_handle: string().optional(),
		source_node_id: string(),
		target_handle: string().optional(),
		target_node_id: string(),
		updated_at: number().optional(),
	})
	.primaryKey("id")

// Original Kysely type: ProjectInvitations
const project_invitations = table("project_invitations")
	.columns({
		created_at: number().optional(),
		email: string(),
		expires_at: number(),
		id: string(),
		invited_by_user_id: string(),
		project_id: string(),
		role: string(),
		status: string(),
		token: string(),
	})
	.primaryKey("id")

// Original Kysely type: ProjectMembers
const project_members = table("project_members")
	.columns({
		created_at: number().optional(),
		id: string(),
		project_id: string(),
		role: string(),
		updated_at: number().optional(),
		user_id: string(),
	})
	.primaryKey("id")

// Original Kysely type: ProjectNodes
const project_nodes = table("project_nodes")
	.columns({
		code: string(),
		created_at: number().optional(),
		id: string(),
		inputs: json(),
		label: string(),
		metadata: json(),
		outputs: json(),
		position: json(),
		project_id: string(),
		type: string(),
		updated_at: number().optional(),
	})
	.primaryKey("id")

// Original Kysely type: Projects
const projects = table("projects")
	.columns({
		bun_lock: string().optional(),
		bun_lock_hash: string().optional(),
		created_at: number().optional(),
		created_by_user_id: string(),
		description: string().optional(),
		id: string(),
		js_dependencies: json(),
		js_runtime_version: number(),
		name: string(),
		python_dependencies: json(),
		python_version: string(),
		settings: json(),
		share_enabled: boolean(),
		share_token: string().optional(),
		slug: string().optional(),
		updated_at: number().optional(),
		uv_lock: string().optional(),
	})
	.primaryKey("id")

// Original Kysely type: PythonPackages
const python_packages = table("python_packages")
	.columns({
		created_at: number().optional(),
		description: string().optional(),
		enabled: boolean().optional(),
		id: string(),
		package_name: string(),
		updated_at: number().optional(),
	})
	.primaryKey("id")

// Original Kysely type: Releases
const releases = table("releases")
	.columns({
		created_at: number().optional(),
		id: string(),
		platforms: json<ReadonlyJson<ReleasePlatforms>>(),
		product_id: string(),
		release_type: string(),
		released_at: number().optional(),
		version: string(),
	})
	.primaryKey("id")

// Original Kysely type: UserProviders
const user_providers = table("user_providers")
	.columns({
		access_token: string().optional(),
		created_at: number(),
		email: string().optional(),
		expires_at: number().optional(),
		id: string(),
		profile: json().optional(),
		provider: string(),
		provider_user_id: string(),
		refresh_token: string().optional(),
		updated_at: number(),
		user_id: string(),
	})
	.primaryKey("id")

// Original Kysely type: Users
const users = table("users")
	.columns({
		created_at: number(),
		email: string(),
		email_verified_at: number().optional(),
		id: string(),
		last_login_at: number().optional(),
		name: string().optional(),
		password_hash: string().optional(),
		updated_at: number(),
	})
	.primaryKey("id")

// ============================================
// Relationships
// ============================================

const geometriesRelationships = relationships(geometries, ({ one }) => ({
	project: one({
		sourceField: ["project_id"],
		destField: ["id"],
		destSchema: projects,
	}),
	project_node: one({
		sourceField: ["project_node_id"],
		destField: ["id"],
		destSchema: project_nodes,
	}),
	user: one({
		sourceField: ["user_id"],
		destField: ["id"],
		destSchema: users,
	}),
}))

const project_edgesRelationships = relationships(project_edges, ({ one }) => ({
	project: one({
		sourceField: ["project_id"],
		destField: ["id"],
		destSchema: projects,
	}),
	source_node: one({
		sourceField: ["source_node_id"],
		destField: ["id"],
		destSchema: project_nodes,
	}),
	target_node: one({
		sourceField: ["target_node_id"],
		destField: ["id"],
		destSchema: project_nodes,
	}),
}))

const project_invitationsRelationships = relationships(
	project_invitations,
	({ one }) => ({
		invited_by_user: one({
			sourceField: ["invited_by_user_id"],
			destField: ["id"],
			destSchema: users,
		}),
		project: one({
			sourceField: ["project_id"],
			destField: ["id"],
			destSchema: projects,
		}),
	}),
)

const project_membersRelationships = relationships(
	project_members,
	({ one }) => ({
		project: one({
			sourceField: ["project_id"],
			destField: ["id"],
			destSchema: projects,
		}),
		user: one({
			sourceField: ["user_id"],
			destField: ["id"],
			destSchema: users,
		}),
	}),
)

const project_nodesRelationships = relationships(
	project_nodes,
	({ one, many }) => ({
		project: one({
			sourceField: ["project_id"],
			destField: ["id"],
			destSchema: projects,
		}),
		geometries: many({
			sourceField: ["id"],
			destField: ["project_node_id"],
			destSchema: geometries,
		}),
		source_edges: many({
			sourceField: ["id"],
			destField: ["source_node_id"],
			destSchema: project_edges,
		}),
		target_edges: many({
			sourceField: ["id"],
			destField: ["target_node_id"],
			destSchema: project_edges,
		}),
	}),
)

const projectsRelationships = relationships(projects, ({ one, many }) => ({
	created_by_user: one({
		sourceField: ["created_by_user_id"],
		destField: ["id"],
		destSchema: users,
	}),
	geometries: many({
		sourceField: ["id"],
		destField: ["project_id"],
		destSchema: geometries,
	}),
	project_edges: many({
		sourceField: ["id"],
		destField: ["project_id"],
		destSchema: project_edges,
	}),
	project_invitations: many({
		sourceField: ["id"],
		destField: ["project_id"],
		destSchema: project_invitations,
	}),
	project_members: many({
		sourceField: ["id"],
		destField: ["project_id"],
		destSchema: project_members,
	}),
	project_nodes: many({
		sourceField: ["id"],
		destField: ["project_id"],
		destSchema: project_nodes,
	}),
}))

const user_providersRelationships = relationships(
	user_providers,
	({ one }) => ({
		user: one({
			sourceField: ["user_id"],
			destField: ["id"],
			destSchema: users,
		}),
	}),
)

const usersRelationships = relationships(users, ({ many }) => ({
	geometries: many({
		sourceField: ["id"],
		destField: ["user_id"],
		destSchema: geometries,
	}),
	project_invitations: many({
		sourceField: ["id"],
		destField: ["invited_by_user_id"],
		destSchema: project_invitations,
	}),
	project_members: many({
		sourceField: ["id"],
		destField: ["user_id"],
		destSchema: project_members,
	}),
	projects: many({
		sourceField: ["id"],
		destField: ["created_by_user_id"],
		destSchema: projects,
	}),
	user_providers: many({
		sourceField: ["id"],
		destField: ["user_id"],
		destSchema: user_providers,
	}),
}))

// ============================================
// Schema
// ============================================

export const schema = createSchema({
	tables: [
		geometries,
		project_edges,
		project_invitations,
		project_members,
		project_nodes,
		projects,
		python_packages,
		releases,
		user_providers,
		users,
	],
	relationships: [
		geometriesRelationships,
		project_edgesRelationships,
		project_invitationsRelationships,
		project_membersRelationships,
		project_nodesRelationships,
		projectsRelationships,
		user_providersRelationships,
		usersRelationships,
	],
	enableLegacyMutators: false,
	enableLegacyQueries: false,
})

export type Schema = typeof schema

// ============================================
// Type Exports
// ============================================

export type GeometriesRow = Row<typeof schema.tables.geometries>
export type Project_edgesRow = Row<typeof schema.tables.project_edges>
export type Project_invitationsRow = Row<
	typeof schema.tables.project_invitations
>
export type Project_membersRow = Row<typeof schema.tables.project_members>
export type Project_nodesRow = Row<typeof schema.tables.project_nodes>
export type ProjectsRow = Row<typeof schema.tables.projects>
export type Python_packagesRow = Row<typeof schema.tables.python_packages>
export type ReleasesRow = Row<typeof schema.tables.releases>
export type User_providersRow = Row<typeof schema.tables.user_providers>
export type UsersRow = Row<typeof schema.tables.users>
